import {
  PRODUCT_BLOCK_LIMITS,
  productBlocksText,
  tidyProductBlocks,
  type BilingualTextDto,
  type HighlightIcon,
  type ProductBlockInput,
  type ProductBlockType,
  type ProductHighlightInput,
} from '@ecom/contracts';

/**
 * The product description's blocks as the editor handles them (ADR-055).
 *
 * Pure, so the rules that decide what is saved — and whether anything changed —
 * are tested without a browser. The editor keeps copy as typed (untrimmed,
 * empty strings); `tidyProductBlocks` from the contracts makes it what the API
 * stores, on the way out and in the "has anything changed" comparison alike.
 */

export type BlockOf<T extends ProductBlockType> = Extract<ProductBlockInput, { type: T }>;

type Language = 'en' | 'ar';

/** Both languages as a form holds them — the shape of `BilingualValue`. */
export interface PlainText {
  en: string;
  ar: string;
}

/** A block type the page shows once: its content is the product's, not written here. */
export const SINGLE_BLOCK_TYPES: readonly ProductBlockType[] = ['looks', 'categories'];

export const emptyText = (): BilingualTextDto => ({ en: '', ar: '' });

/** Copy as a form field reads it: never null. */
export const asPlain = (text: BilingualTextDto | null | undefined): PlainText => ({ en: text?.en ?? '', ar: text?.ar ?? '' });

/**
 * A block id the API accepts (/^[A-Za-z0-9_-]{1,40}$/). `randomUUID` needs a
 * secure context, and an admin opened over plain http on a LAN address has
 * only `getRandomValues` — so that is the fallback, not an error.
 */
export function newBlockId(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();
  if (typeof source?.getRandomValues === 'function') {
    return Array.from(source.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** A card with nothing chosen yet: written here unless it starts from a specification. */
export function newHighlight(attribute: string | null = null, icon: HighlightIcon | null = null): ProductHighlightInput {
  return { icon, attribute, label: emptyText(), value: emptyText() };
}

/** A new, empty block of a type — what "add block" puts on the page. */
export function newBlock(type: ProductBlockType, id: string = newBlockId()): ProductBlockInput {
  switch (type) {
    case 'text':
      return { id, type, heading: emptyText(), body: emptyText() };
    case 'photos':
      return { id, type, photos: [] };
    case 'highlights':
      return { id, type, items: [newHighlight()] };
    case 'list':
      // One empty point: a list is written point by point, and the first row invites the first.
      return { id, type, heading: emptyText(), style: 'bulleted', points: [emptyText()] };
    case 'looks':
    case 'categories':
      return { id, type, heading: emptyText() };
  }
}

/** The page as the editor starts it from a plain description: that description, in one text block. */
export function blocksFromDescription(description: PlainText, id: string = newBlockId()): ProductBlockInput[] {
  return [{ id, type: 'text', heading: emptyText(), body: { en: description.en.trim(), ar: description.ar.trim() } }];
}

/** The blocks' words per language — what the API would keep as the plain description. */
export function blocksPlainText(blocks: readonly ProductBlockInput[] | null): PlainText {
  const tidy = tidyProductBlocks(blocks);
  return { en: productBlocksText(tidy, 'en') ?? '', ar: productBlocksText(tidy, 'ar') ?? '' };
}

/** Whether another block of this type fits: the page's limit, and looks and categories once each. */
export function canAddBlock(blocks: readonly ProductBlockInput[], type: ProductBlockType): boolean {
  if (blocks.length >= PRODUCT_BLOCK_LIMITS.blocks) return false;
  return !(SINGLE_BLOCK_TYPES.includes(type) && blocks.some((block) => block.type === type));
}

/** One entry moved from one place to another; out of range leaves the list as it was. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * A copy of the block placed right after it, with an id of its own and no
 * copy shared with the original — editing one must never edit the other.
 * Looks and categories are not copied: a page shows each once.
 */
export function duplicateBlock(blocks: readonly ProductBlockInput[], index: number, id: string = newBlockId()): ProductBlockInput[] {
  const block = blocks[index];
  if (!block || !canAddBlock(blocks, block.type)) return [...blocks];
  const copy = { ...(JSON.parse(JSON.stringify(block)) as ProductBlockInput), id };
  return [...blocks.slice(0, index + 1), copy, ...blocks.slice(index + 1)];
}

const other = (language: Language): Language => (language === 'en' ? 'ar' : 'en');

/** The copy in one language, else the other: a summary is for finding a block, not proofreading it. */
function pick(text: BilingualTextDto | null | undefined, language: Language): string | null {
  const own = text?.[language]?.trim();
  if (own) return own;
  return text?.[other(language)]?.trim() || null;
}

/** The first words of a text, on one line, cut at a word. */
export function firstWords(text: string, max = 80): string {
  const line = text.replace(/\s+/g, ' ').trim();
  if (line.length <= max) return line;
  const cut = line.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * What a folded block's header says, in the reader's language: some words
 * (a heading, the first words, the cards' labels) and a count where a block
 * holds several things. Either may be missing; the header words the rest.
 */
export function blockSummary(
  block: ProductBlockInput,
  language: Language,
  specificationLabel: (code: string) => string | undefined = () => undefined,
): { text: string | null; count: number | null } {
  switch (block.type) {
    case 'text': {
      const words = pick(block.heading, language) ?? pick(block.body, language);
      return { text: words ? firstWords(words) : null, count: null };
    }
    case 'photos':
      return { text: null, count: block.photos.length > 0 ? block.photos.length : null };
    case 'highlights': {
      const labels = block.items
        .map((item) =>
          item.attribute
            ? (pick(item.label, language) ?? specificationLabel(item.attribute) ?? item.attribute)
            : (pick(item.label, language) ?? pick(item.value, language)),
        )
        .filter((label): label is string => Boolean(label));
      return { text: labels.length > 0 ? firstWords(labels.join(' · ')) : null, count: null };
    }
    case 'list': {
      const points = block.points.filter((point) => pick(point, language) !== null).length;
      const heading = pick(block.heading, language);
      return { text: heading ? firstWords(heading) : null, count: points > 0 ? points : null };
    }
    case 'looks':
    case 'categories': {
      const heading = pick(block.heading, language);
      return { text: heading ? firstWords(heading) : null, count: null };
    }
    default:
      return { text: null, count: null };
  }
}

/**
 * The icon a card reading a specification most likely wants, from the
 * specification's code — a suggestion the operator can change, never a rule.
 */
const ICON_HINTS: [RegExp, HighlightIcon][] = [
  [/drape|fall|flow/, 'drape'],
  [/season|climate|weather/, 'season'],
  [/width/, 'width'],
  [/weight|gsm/, 'weight'],
  [/composition|material|fib(re|er)|content/, 'composition'],
  [/care|wash|iron/, 'care'],
  [/origin|country/, 'origin'],
  [/quality|grade/, 'quality'],
  [/natural|organic/, 'natural'],
  [/durab|strength|warranty/, 'durable'],
];

export function suggestIcon(code: string): HighlightIcon | null {
  const lower = code.toLowerCase();
  return ICON_HINTS.find(([pattern]) => pattern.test(lower))?.[1] ?? null;
}

// ─── Reading what the API sent ───────────────────────────────────────────────

const readText = (value: unknown): BilingualTextDto => {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return { en: typeof record.en === 'string' ? record.en : null, ar: typeof record.ar === 'string' ? record.ar : null };
};

/**
 * The stored blocks, every field present, so the editor never meets a block
 * missing half its shape. No blocks — or an empty list — is `null`: the
 * theme's standard layout (ADR-055). A type this admin does not know is kept
 * as it came, to be moved or removed but not lost.
 */
export function readBlocks(raw: readonly ProductBlockInput[] | null | undefined): ProductBlockInput[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((entry): ProductBlockInput => {
    const block = entry as ProductBlockInput & Record<string, unknown>;
    switch (block.type) {
      case 'text':
        return { id: block.id, type: 'text', heading: readText(block.heading), body: readText(block.body) };
      case 'photos':
        return {
          id: block.id,
          type: 'photos',
          photos: (Array.isArray(block.photos) ? block.photos : []).map((photo) => ({
            imageId: photo.imageId,
            caption: readText(photo.caption),
            closeUp: photo.closeUp === true,
          })),
        };
      case 'highlights':
        return {
          id: block.id,
          type: 'highlights',
          items: (Array.isArray(block.items) ? block.items : []).map((item) => ({
            icon: item.icon ?? null,
            attribute: item.attribute ?? null,
            label: readText(item.label),
            value: readText(item.value),
          })),
        };
      case 'list':
        return {
          id: block.id,
          type: 'list',
          heading: readText(block.heading),
          style: block.style === 'numbered' ? 'numbered' : 'bulleted',
          points: (Array.isArray(block.points) ? block.points : []).map(readText),
        };
      case 'looks':
      case 'categories':
        return { id: block.id, type: block.type, heading: readText(block.heading) };
      default:
        return block;
    }
  });
}

// ─── What is saved ───────────────────────────────────────────────────────────

/** The description's part of a product save: the plain text, the blocks, or both. */
export interface DescriptionPayload {
  /** The plain description per language; absent when the blocks write it (the API derives it). */
  description?: { en: string | null; ar: string | null };
  /** Absent keeps what is stored; null is the theme's standard layout; a list is the page. */
  descriptionBlocks?: ProductBlockInput[] | null;
}

/**
 * What a save sends for the description (ADR-055).
 *
 * - **No blocks** (the standard layout): the plain description as typed, and
 *   `descriptionBlocks: null` only when the product had blocks — switching
 *   back. A product that never had any is not told so on every save.
 * - **Blocks with something in them**: the tidied blocks, and no plain
 *   description — the API writes it from their words.
 * - **Blocks with nothing in them**: the page has no words, so the plain
 *   description is cleared, and a product that had blocks goes back to the
 *   standard layout. Sending only `null` would bring back the words of the
 *   blocks just emptied, and the form would never read as saved.
 *
 * `hadBlocks` is what the loaded product holds, so the saved form and the
 * form as typed are compared on the same terms.
 */
export function descriptionPayload(
  description: PlainText,
  blocks: readonly ProductBlockInput[] | null,
  hadBlocks: boolean,
): DescriptionPayload {
  const standard = hadBlocks ? { descriptionBlocks: null } : {};
  if (blocks === null) {
    return { description: { en: description.en.trim() || null, ar: description.ar.trim() || null }, ...standard };
  }
  const tidy = tidyProductBlocks(blocks);
  if (tidy === null) return { description: { en: null, ar: null }, ...standard };
  return { descriptionBlocks: tidy };
}
